/**
 * Hook for managing PRD breakdown status and operations.
 *
 * Provides real-time status polling, cancellation, and cross-channel visibility
 * for breakdowns initiated via web UI, MCP, or CLI.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelBreakdown,
  type CancelBreakdownResponse,
  getBreakdownStatusBatch,
  type ProjectBreakdownStatusBatchItem,
} from '~/services/api/prd';

export interface UsePRDBreakdownBatchResult {
  batchData:
    | {
        projects: ProjectBreakdownStatusBatchItem[];
      }
    | undefined;
  isLoading: boolean;
}

export interface UsePRDBreakdownResult {
  status: ProjectBreakdownStatusBatchItem | undefined;
  isLoading: boolean;
  cancel: () => Promise<CancelBreakdownResponse>;
  isCanceling: boolean;
}

/**
 * Hook for reading PRD breakdown status for a single project.
 *
 * This hook reads from the cache populated by `usePRDBreakdownBatch`.
 * It does NOT make individual API calls - all data comes from the batch query.
 *
 * Features:
 * - Zero API calls (reads from batch cache)
 * - Instant data access
 * - Cancellation with query invalidation
 *
 * @param projectId - Project UUID to track breakdown for
 * @returns Breakdown status, loading state, and cancel function
 *
 * @example
 * const { status, isLoading, cancel, isCanceling } = usePRDBreakdown(projectId);
 *
 * if (status?.has_active_breakdown) {
 *   // Show status panel
 * }
 *
 * await cancel(); // Soft cancel (warns user)
 */
export function usePRDBreakdown(projectId: string): UsePRDBreakdownResult {
  const queryClient = useQueryClient();

  // Read from cache populated by batch query (no API call)
  const { data: status, isLoading } = useQuery<ProjectBreakdownStatusBatchItem>({
    queryKey: ['prd-breakdown-status', projectId],
    queryFn: () => {
      // This should never be called due to enabled: false
      // It only reads from cache populated by usePRDBreakdownBatch
      throw new Error('usePRDBreakdown should only read from cache, not fetch');
    },
    enabled: false, // Never fetch - only read from cache
    staleTime: Infinity, // Never auto-refetch (batch query handles updates)
  });

  // Cancel uses projectId (not jobId) - backend finds active job automatically
  const cancelMutation = useMutation({
    mutationFn: () => cancelBreakdown(projectId),
    onSuccess: () => {
      // Invalidate batch query to refresh all project statuses
      void queryClient.invalidateQueries({ queryKey: ['prd-breakdown-status-batch'] });
      // Invalidate projects list to update any project-level indicators
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return {
    status,
    isLoading,
    cancel: cancelMutation.mutateAsync,
    isCanceling: cancelMutation.isPending,
  };
}

/**
 * Hook for fetching all project breakdown statuses in a single batch query.
 *
 * This is the primary hook that should be called once at the project list level.
 * Individual ProjectCards then use `usePRDBreakdown(projectId)` which reads from this cache.
 *
 * Features:
 * - Single batch query instead of N individual queries
 * - Automatic polling every 5 seconds when any breakdown is active
 * - Populates individual project caches for instant reads
 *
 * @returns Batch status data, loading state, and refetch function
 *
 * @example
 * // In ProjectList component:
 * const { batchData, isLoading } = usePRDBreakdownBatch();
 *
 * // In ProjectCard component:
 * const { status } = usePRDBreakdown(projectId); // Reads from batch cache
 */
export function usePRDBreakdownBatch(): UsePRDBreakdownBatchResult {
  const queryClient = useQueryClient();

  const { data: batchData, isLoading } = useQuery({
    queryKey: ['prd-breakdown-status-batch'],
    queryFn: async () => {
      const batch = await getBreakdownStatusBatch();

      // Populate individual project caches for instant reads
      batch.projects.forEach(projectStatus => {
        queryClient.setQueryData(['prd-breakdown-status', projectStatus.project_id], projectStatus);
      });

      return batch;
    },
    refetchInterval: query => {
      // Poll every 5 seconds if ANY project has an active breakdown
      const hasActiveBreakdown = query.state.data?.projects.some(p => p.has_active_breakdown);

      // Also check if any job completed/failed in the last 30 seconds
      // This ensures the UI captures final status even if it happens between polls
      const now = Date.now();
      const hasRecentlyCompleted = query.state.data?.projects.some(p => {
        const completedAt = p.breakdown_completed_at
          ? new Date(p.breakdown_completed_at).getTime()
          : 0;
        const isFailed = p.breakdown_status === 'FAILED';
        const isCancelled = p.breakdown_status === 'CANCELLED';
        const isRecent = completedAt > 0 && now - completedAt < 30000; // 30 seconds
        return isFailed || isCancelled || isRecent;
      });

      return hasActiveBreakdown || hasRecentlyCompleted ? 5000 : false;
    },
    staleTime: 10000, // Keep data fresh for 10 seconds
  });

  return {
    batchData,
    isLoading,
  };
}
